/**
 * What a scope's git operation is doing right now (owner, 05.10.2026: with
 * many boards "Connecting…" sat for a long time with no sign of life). The
 * steps of connect / sync / pull report here; the dialog and the row show
 * "Reading boards · 12 of 40". One entry per scope key, cleared when the
 * operation ends.
 *
 * step: 'projects' | 'boards' | 'tasks' | 'documents' | 'writing' | 'scanning' | 'committing' | 'pushing' | 'merging'
 */
import { useSyncExternalStore } from 'react';

const current = new Map();
/** Scopes with a user-started operation: the background refresh runs the same readers and reports nothing. */
const active = new Set();
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

/** @param {{ step: string, done?: number, total?: number }} progress */
export function reportScopeProgress(key, progress) {
  if (key == null || !active.has(String(key))) return;
  current.set(String(key), { ...progress });
  emit();
}

/** Run `work` with progress shown for `key`; cleared when it ends, however it ends. */
export async function withScopeProgress(key, work) {
  const k = String(key);
  active.add(k);
  try {
    return await work();
  } finally {
    active.delete(k);
    if (current.delete(k)) emit();
  }
}

export const getScopeProgress = (key) => (key == null ? null : current.get(String(key)) || null);

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The live progress of one scope's operation, or null. */
export function useScopeProgress(key) {
  return useSyncExternalStore(subscribe, () => getScopeProgress(key));
}

const LABELS = {
  projects: 'Reading projects',
  boards: 'Reading boards',
  tasks: 'Reading tasks',
  documents: 'Reading documents',
  writing: 'Writing files to the folder',
  scanning: 'Checking for passwords and keys',
  committing: 'Saving a version',
  pushing: 'Pushing to the repository',
  merging: 'Bringing in the repository’s changes',
};

/** "Reading boards · 12 of 40" — or just the step when there is no count. */
export function progressText(progress) {
  if (!progress) return '';
  const label = LABELS[progress.step] || progress.step;
  return progress.total > 0 ? `${label} · ${Math.min(progress.done || 0, progress.total)} of ${progress.total}` : label;
}

/** Report a counted loop: `const tick = counter(key, 'boards', rows.length)`, then `tick()` after each item. */
export function counter(key, step, total) {
  let done = 0;
  reportScopeProgress(key, { step, done, total });
  return () => {
    done += 1;
    reportScopeProgress(key, { step, done, total });
  };
}
